import React, { useState } from 'react';
import { Anchor, Edit2, MapPin, Gauge, Search } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { VesselMap } from '@/components/map/VesselMap';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';

export default function StasiunPanduPage() {
  const { stations, selectStation, selectedStation, setStations } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);

  const filteredStations = stations.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.areaType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAreaTypeColor = (type) => {
    switch (type) {
      case 'pilot': return 'bg-warning/10 text-warning border-warning/30';
      case 'anchorage': return 'bg-success/10 text-success border-success/30';
      case 'harbor': return 'bg-primary/10 text-primary border-primary/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getAreaTypeLabel = (type) => {
    switch (type) {
      case 'pilot': return 'Pilot Zone';
      case 'anchorage': return 'Anchorage';
      case 'harbor': return 'Harbor';
      default: return type;
    }
  };

  const handleEdit = (station) => {
    setEditingStation({ ...station });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingStation) {
      setStations(prev => prev.map(s => 
        s.id === editingStation.id ? editingStation : s
      ));
      setEditDialogOpen(false);
      setEditingStation(null);
    }
  };

  const handleViewOnMap = (station) => {
    selectStation(station);
  };

  return (
    <DashboardLayout title="Stasiun Pandu">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        {/* Left - Station List */}
        <div className="flex flex-col gap-4">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Anchor className="w-5 h-5 text-primary" />
                  Daftar Stasiun
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {stations.length} Stations
                </Badge>
              </div>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cari stasiun..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </CardHeader>

            <CardContent className="flex-1 p-0">
              <div className="overflow-auto max-h-[calc(100vh-340px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs">Nama Stasiun</TableHead>
                      <TableHead className="text-xs">Area Type</TableHead>
                      <TableHead className="text-xs">Max Speed</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStations.map((station) => (
                      <TableRow
                        key={station.id}
                        className={cn(
                          "transition-colors cursor-pointer",
                          selectedStation?.id === station.id && "bg-accent"
                        )}
                        onClick={() => handleViewOnMap(station)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Anchor className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">{station.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn("text-xs", getAreaTypeColor(station.areaType))}
                          >
                            {getAreaTypeLabel(station.areaType)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Gauge className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm font-mono">{station.maxSpeed} kn</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewOnMap(station);
                              }}
                            >
                              <MapPin className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(station);
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Stats by Area Type */}
          <div className="grid grid-cols-3 gap-3">
            {['pilot', 'anchorage', 'harbor'].map((type) => {
              const count = stations.filter(s => s.areaType === type).length;
              return (
                <Card key={type} className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      type === 'pilot' && "bg-warning/10",
                      type === 'anchorage' && "bg-success/10",
                      type === 'harbor' && "bg-primary/10"
                    )}>
                      <Anchor className={cn(
                        "w-5 h-5",
                        type === 'pilot' && "text-warning",
                        type === 'anchorage' && "text-success",
                        type === 'harbor' && "text-primary"
                      )} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground capitalize">{getAreaTypeLabel(type)}</p>
                      <p className="text-2xl font-semibold">{count}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Right - Map */}
        <Card className="overflow-hidden">
          <div className="h-full min-h-[500px]">
            <VesselMap
              showGeofences={false}
              showStations={true}
            />
          </div>
        </Card>
      </div>

      {/* Edit Station Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Station</DialogTitle>
            <DialogDescription>
              Update station information and speed limits.
            </DialogDescription>
          </DialogHeader>

          {editingStation && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Station Name</Label>
                <Input
                  value={editingStation.name}
                  onChange={(e) => setEditingStation({ ...editingStation, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Area Type</Label>
                  <Select
                    value={editingStation.areaType}
                    onValueChange={(value) => setEditingStation({ ...editingStation, areaType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pilot">Pilot Zone</SelectItem>
                      <SelectItem value="anchorage">Anchorage</SelectItem>
                      <SelectItem value="harbor">Harbor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Max Speed (knots)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={editingStation.maxSpeed}
                    onChange={(e) => setEditingStation({ ...editingStation, maxSpeed: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Boundary coordinates: {editingStation.boundaryCoords.length} points
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
